import { useEffect, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { Selection } from "./useChatData";
import Avatar from "./Avatar";
import ConfirmModal from "./ConfirmModal";
import { PersonPlusIcon, LeaveIcon, TrashIcon } from "./Icons";
import { fetchVisibleUsers, emailForUid } from "./directoryScope";
import { parseMeta } from "./status";
import * as sounds from "./sounds";

/**
 * Everything about who's in a channel/group, in one place: the participant
 * list with roles, adding people, promoting/demoting admins, removing
 * someone, and leaving or deleting.
 *
 * Opened from the header facepile (Teams-style) rather than living in the
 * Details panel, where the member list and a stack of destructive buttons
 * sat awkwardly alongside unrelated info like the files list.
 */

type ConfirmState =
  | { kind: "kick"; uid: string; name: string }
  | { kind: "leave" }
  | { kind: "delete" }
  | null;

const SCOPE_LABEL: Record<string, string> = { admin: "Admin", moderator: "Moderator" };

export default function MembersPanel({
  selection,
  members,
  myScope,
  owner,
  meUid,
  onMembersChanged,
  onScopeChanged,
  onMemberRemoved,
  onLeft,
  onClose,
}: {
  selection: Selection;
  members: CometChat.GroupMember[];
  myScope?: string | null;
  owner?: string | null;
  meUid?: string;
  onMembersChanged: () => void;
  /** Applies a promote/demote to local state immediately, rather than
   * waiting on a server refetch — see useGroupMembers' patchScope. */
  onScopeChanged: (uid: string, scope: string) => void;
  /** Drops a removed member from local state immediately — see
   * useGroupMembers' removeMemberLocal. */
  onMemberRemoved: (uid: string) => void;
  onLeft: () => void;
  onClose: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Same authoritative-scope reasoning as the Details panel: getGroup()'s
  // own answer, with owner as a fallback so a creator is never locked out
  // of managing their own group by a stale scope.
  const isOwner = !!meUid && owner === meUid;
  const isAdmin = myScope === "admin" || isOwner;
  const canManage = isAdmin || myScope === "moderator";
  const label = selection.kind === "group" && selection.isStructuredChannel ? "channel" : "group";

  const kick = async (uid: string) => {
    setBusyUid(uid);
    try {
      await CometChat.kickGroupMember(selection.id, uid);
      onMemberRemoved(uid);
      onMembersChanged();
    } catch (e) {
      console.warn("kick failed", e);
    } finally {
      setBusyUid(null);
    }
  };

  const setScope = async (uid: string, scope: string) => {
    setBusyUid(uid);
    sounds.playOnce(sounds.SOUND.click);
    try {
      await CometChat.updateGroupMemberScope(selection.id, uid, scope as CometChat.GroupMemberScope);
      onScopeChanged(uid, scope);
      onMembersChanged();
    } catch (e) {
      console.warn("scope change failed", e);
    } finally {
      setBusyUid(null);
    }
  };

  const runConfirm = async () => {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      if (confirmState.kind === "kick") {
        await kick(confirmState.uid);
      } else if (confirmState.kind === "leave") {
        await CometChat.leaveGroup(selection.id).then(onLeft).catch((e) => console.warn("leave failed", e));
      } else {
        await CometChat.deleteGroup(selection.id).then(onLeft).catch((e) => console.warn("delete failed", e));
      }
    } finally {
      setConfirmBusy(false);
      setConfirmState(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="members-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <b>
            {selection.isStructuredChannel && "#"}
            {selection.name}
          </b>
          <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="members-sub">
          <span>
            {members.length} {members.length === 1 ? "participant" : "participants"}
          </span>
          {canManage && (
            <button
              className="members-add"
              onClick={() => {
                sounds.playOnce(sounds.SOUND.click);
                setShowAdd(true);
              }}
            >
              <PersonPlusIcon size={15} />
              <span>Add people</span>
            </button>
          )}
        </div>

        <div className="members-list">
          {members.length === 0 && <div className="sect-empty">No participants loaded</div>}
          {members.map((m) => {
            const uid = m.getUid();
            const scope = m.getScope?.() || "participant";
            const isMe = uid === meUid;
            const memberIsOwner = owner === uid;
            const busy = busyUid === uid;
            return (
              <div className="members-row" key={uid}>
                <Avatar uid={uid} name={m.getName()} size="dav" />
                <span className="members-row__name">
                  {m.getName()}
                  {isMe && <span className="members-row__you">you</span>}
                </span>
                {SCOPE_LABEL[scope] && (
                  <span className={`members-row__role ${scope === "admin" ? "is-admin" : ""}`}>
                    {SCOPE_LABEL[scope]}
                  </span>
                )}
                {/* Always rendered, reserving the same width whether or
                    not this particular row has buttons in it — otherwise
                    a row with no actions (yourself, the owner) has its
                    role pill sitting flush against the edge while a row
                    WITH actions pushes its pill further left to make room,
                    so the pills never lined up from one row to the next. */}
                <div className="members-row__actions">
                  {/* The owner's role can't be handed off or revoked here —
                      demoting them would leave the group with no
                      guaranteed admin, so that stays out of this panel
                      entirely. */}
                  {canManage && !isMe && !memberIsOwner && (
                    <>
                      {isAdmin && (
                        <button
                          className="members-row__btn"
                          disabled={busy}
                          onClick={() => void setScope(uid, scope === "admin" ? "participant" : "admin")}
                        >
                          {scope === "admin" ? "Remove admin" : "Make admin"}
                        </button>
                      )}
                      <button
                        className="members-row__btn members-row__btn--danger"
                        disabled={busy}
                        onClick={() => setConfirmState({ kind: "kick", uid, name: m.getName() })}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="members-foot">
          <button
            className="details-action"
            onClick={() => {
              sounds.playOnce(sounds.SOUND.click);
              setConfirmState({ kind: "leave" });
            }}
          >
            <LeaveIcon size={16} />
            <span>Leave {label}</span>
          </button>
          {isAdmin && (
            <button
              className="details-action details-action--danger"
              onClick={() => {
                sounds.playOnce(sounds.SOUND.click);
                setConfirmState({ kind: "delete" });
              }}
            >
              <TrashIcon size={16} />
              <span>Delete {label}</span>
            </button>
          )}
        </div>
      </div>

      {confirmState && (
        <ConfirmModal
          title={
            confirmState.kind === "kick"
              ? `Remove ${confirmState.name} from ${selection.name}?`
              : confirmState.kind === "leave"
              ? `Leave ${selection.name}?`
              : `Delete ${selection.name} for everyone?`
          }
          body={confirmState.kind === "delete" ? "This can't be undone." : undefined}
          confirmLabel={
            confirmState.kind === "kick" ? "Remove" : confirmState.kind === "leave" ? "Leave" : "Delete"
          }
          danger={confirmState.kind !== "leave"}
          busy={confirmBusy}
          onConfirm={() => void runConfirm()}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {showAdd && (
        <AddMemberModal
          guid={selection.id}
          existing={members.map((m) => m.getUid())}
          onClose={() => setShowAdd(false)}
          onAdded={onMembersChanged}
        />
      )}
    </div>
  );
}

/** Pick users to add to a channel. */
function AddMemberModal({
  guid,
  existing,
  onClose,
  onAdded,
}: {
  guid: string;
  existing: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [users, setUsers] = useState<CometChat.User[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchVisibleUsers()
      .then((list) => {
        if (!cancelled) setUsers(list.filter((u) => !existing.includes(u.getUid())));
      })
      .catch((e) => console.warn("add-member user list failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid]);

  const add = async (u: CometChat.User) => {
    if (adding) return;
    sounds.playOnce(sounds.SOUND.click);
    setAdding(u.getUid());
    try {
      const member = new CometChat.GroupMember(u.getUid(), "participant");
      await CometChat.addMembersToGroup(guid, [member], []);
      onAdded();
      setUsers((prev) => prev.filter((x) => x.getUid() !== u.getUid()));
    } catch (e) {
      console.warn("add member failed", e);
    } finally {
      setAdding(null);
    }
  };

  const filtered = filter.trim()
    ? users.filter((u) => {
        const needle = filter.trim().toLowerCase();
        return (
          (u.getName() || "").toLowerCase().includes(needle) ||
          (emailForUid(u.getUid()) || parseMeta(u).dbmEmail || "").toLowerCase().includes(needle)
        );
      })
    : users;

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 60 }}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <b>Add people</b>
          <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-section">
          <div className="modal-row">
            <input
              type="text"
              placeholder="Search people…"
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="modal-list">
            {loading && <div className="sect-empty">Loading…</div>}
            {!loading && users.length === 0 && (
              <div className="sect-empty">Everyone's already in here</div>
            )}
            {!loading && users.length > 0 && filtered.length === 0 && (
              <div className="sect-empty">No matches</div>
            )}
            {filtered.map((u) => (
              <button
                key={u.getUid()}
                className="modal-user"
                disabled={!!adding}
                onClick={() => void add(u)}
              >
                <Avatar uid={u.getUid()} name={u.getName()} size="dav" />
                <span className="nm">{adding === u.getUid() ? "Adding…" : u.getName()}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
